import { Component, EventEmitter, OnInit, OnDestroy, Output } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { DropdownModule } from 'primeng/dropdown';
import { InputTextareaModule } from 'primeng/inputtextarea';

import { OperationsService, RequestMissingDocumentRequest } from '../../../../../services/operations/operations.service';
import { DocumentCategoryEnum } from '../../../../../models/student-document-vault/student-document-vault';

@Component({
  selector: 'app-request-missing-document-modal',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    DialogModule,
    ButtonModule,
    InputTextModule,
    DropdownModule,
    InputTextareaModule
  ],
  templateUrl: './request-missing-document-modal.component.html',
  styleUrls: ['./request-missing-document-modal.component.scss']
})
export class RequestMissingDocumentModalComponent implements OnInit, OnDestroy {
  @Output() onRequested = new EventEmitter<void>();

  // State
  private destroy$ = new Subject<void>();
  visible: boolean = false;
  requestForm!: FormGroup;
  isSubmitting: boolean = false;
  studentId: string = '';

  // Dropdown Data
  categories = [
    { label: 'Identity Documents', value: DocumentCategoryEnum.Identity },
    { label: 'Academics', value: DocumentCategoryEnum.Academics },
    { label: 'Essays', value: DocumentCategoryEnum.EssayPilot },
    { label: 'Letter of Recommendation', value: DocumentCategoryEnum.LOR },
    { label: 'Test Scores', value: DocumentCategoryEnum.TestScores },
    { label: 'Other Documents', value: DocumentCategoryEnum.Other }
  ];

  constructor(
    private operationsService: OperationsService,
    private fb: FormBuilder,
    private messageService: MessageService
  ) {}

  ngOnInit(): void {
    this.initForm();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  initForm(): void {
    this.requestForm = this.fb.group({
      Title: ['', Validators.required],
      Category: [DocumentCategoryEnum.Identity, Validators.required],
      Remarks: ['']
    });
  }

  show(studentId: string, category?: DocumentCategoryEnum): void {
    try {
      this.studentId = studentId;
      if (this.requestForm) {
        this.requestForm.reset({
          Category: category || DocumentCategoryEnum.Identity
        });
      }
      this.visible = true;
    } catch (err) {
      console.error('Error opening request modal:', err);
    }
  }

  hide(): void {
    this.visible = false;
    this.isSubmitting = false;
    this.requestForm.reset({
      Category: DocumentCategoryEnum.Identity
    });
  }



  submitRequest(): void {
    if (this.requestForm.invalid) {
      this.requestForm.markAllAsTouched();
      return;
    }

    this.isSubmitting = true;
    const formValues = this.requestForm.value;

    const request: RequestMissingDocumentRequest = {
      studentId: this.studentId,
      title: formValues.Title,
      category: formValues.Category,
      remarks: formValues.Remarks
    };

    this.operationsService.requestMissingDocument(request)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Missing document request sent' });
          this.onRequested.emit();
          this.hide(); // Use hide() to ensure clean state reset
        },
        error: () => {
          this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to send request' });
          this.isSubmitting = false;
        }
      });
  }
}
